import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../../components/useBreadcrumbBack";
import { useAuth } from "../../../../providers/AuthProvider";
import { useFormFeedback } from "../../../../hooks/useFormFeedback";
import { ApiError } from "../../../../services/apiClient";
import { listSessions, revokeAllSessions, revokeSession } from "../../../auth/api/auth";
import type { SessionView } from "../../../../types/auth";

import { ConfirmDialog } from "../../../../components/ConfirmDialog";
import { PageShell } from "../../../shared/layouts/PageShell";
import { PageHeader } from "../../../shared/ui/layouts/PageHeader";
import { defaultIconProps } from "../../../shared/ui/icons";
import { SkeletonList } from "../../../shared/ui/skeletons";
import { createLogger } from "../../../../utils/logger";

import { ArrowsClockwise, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";

const logger = createLogger("ProfileSessionsPage");

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const heroContentClasses = "max-w-3xl";
const heroTitleClasses = "text-4xl font-semibold text-slate-900 dark:text-slate-100";
const actionBarClasses = "flex flex-wrap gap-3";
const refreshButtonClasses =
  "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";
const dangerButtonClasses =
  "inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 disabled:opacity-60 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/40";
const ghostButtonClasses =
  "inline-flex items-center gap-2 rounded-md border border-transparent bg-transparent px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:text-slate-400 dark:hover:bg-slate-800";
const cardClasses =
  "flex flex-col gap-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900";
const listStackClasses = "flex flex-col gap-4";
const rowClasses =
  "flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 md:flex-row md:items-start md:justify-between dark:border-slate-800 dark:bg-slate-800/50";
const metaTitleClasses = "flex items-center gap-3 text-slate-800 dark:text-slate-200";
const pillClasses =
  "rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400";
const metaSmallClasses = "mt-1 text-sm text-slate-500 dark:text-slate-400";
const placeholderClasses = "text-sm text-slate-500 dark:text-slate-400";
const revokeButtonClasses =
  "inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 disabled:opacity-60 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/40";

export function ProfileSessionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, accessToken, logout, sessionId } = useAuth();
  const { setError, setSuccess, resetFeedback } = useFormFeedback();
  const [sessions, setSessions] = useState<SessionView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);
  const [isRevokingAll, setIsRevokingAll] = useState(false);
  const [showRevokeAllConfirm, setShowRevokeAllConfirm] = useState(false);
  const [sessionToConfirm, setSessionToConfirm] = useState<SessionView | null>(null);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("settings.profile.title"), to: "/settings/profile" },
      { label: t("settings.profile.sessions.title", "Active sessions") },
    ];
  }, [t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/settings/profile" },
  });

  const loadSessions = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setIsLoading(true);
    resetFeedback();
    try {
      const data = await listSessions(accessToken);
      setSessions(data);
    } catch (err) {
      logger.error("Failed to load sessions", err);
      setError(
        err instanceof ApiError
          ? err.message
          : t("settings.profile.sessions.loadError", "Unable to load sessions"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, resetFeedback, setError, t]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const handleRevokeSession = useCallback(
    async (sessionId: string, options?: { redirectOnRevoke?: boolean }) => {
      if (!accessToken) {
        return;
      }
      setRevokingSessionId(sessionId);
      resetFeedback();
      try {
        await revokeSession(accessToken, sessionId);
        if (options?.redirectOnRevoke) {
          setRevokingSessionId(null);
          await logout();
          navigate("/login", { replace: true });
          return;
        }
        setSessions((prev) => prev.filter((session) => session.id !== sessionId));
        setSuccess(t("settings.profile.sessions.revoked", "Session revoked"));
      } catch (err) {
        logger.error("Failed to revoke session", err);
        setError(
          err instanceof ApiError
            ? err.message
            : t("settings.profile.sessions.revokeError", "Unable to revoke this session"),
        );
      } finally {
        setRevokingSessionId(null);
      }
    },
    [accessToken, logout, navigate, resetFeedback, setError, setSuccess, t]
  );

  const handleRevokeAllSessions = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setIsRevokingAll(true);
    resetFeedback();
    try {
      await revokeAllSessions(accessToken);
      setSessions([]);
      setSuccess(t("settings.profile.sessions.revoked_all", "Signed out of all sessions"));
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      logger.error("Failed to revoke all sessions", err);
      setError(
        err instanceof ApiError
          ? err.message
          : t("settings.profile.sessions.revokeAllError", "Unable to sign out of other sessions"),
      );
    } finally {
      setIsRevokingAll(false);
    }
  }, [accessToken, logout, navigate, resetFeedback, setError, setSuccess, t]);

  const confirmRevokeAllSessions = useCallback(async () => {
    setShowRevokeAllConfirm(false);
    await handleRevokeAllSessions();
  }, [handleRevokeAllSessions]);

  if (!user || !accessToken) {
    return null;
  }

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={t("settings.profile.sessions.title", "Active sessions")}
        subtitle={t(
          "settings.profile.sessions.description",
          "Review signed-in devices and revoke access you no longer recognize.",
        )}
        contentClassName={heroContentClasses}
        titleClassName={heroTitleClasses}
        actions={
          <div className={actionBarClasses}>
            <button
              type="button"
              className={refreshButtonClasses}
              onClick={loadSessions}
              disabled={isLoading || isRevokingAll}
            >
              <ArrowsClockwise {...defaultIconProps} aria-hidden />
              {isLoading
                ? t("actions.refreshing", "Refreshing…")
                : t("actions.refresh", "Refresh")}
            </button>
            <button
              type="button"
              className={dangerButtonClasses}
              onClick={() => setShowRevokeAllConfirm(true)}
              disabled={isRevokingAll || sessions.length === 0}
            >
              <ShieldCheck {...defaultIconProps} aria-hidden />
              {isRevokingAll
                ? t("settings.profile.sessions.revoking_all", "Signing out…")
                : t("settings.profile.sessions.revoke_all", "Sign out everywhere")}
            </button>
            <button type="button" className={ghostButtonClasses} onClick={handleBack}>
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      <section className={cardClasses}>
        <div className="flex flex-col gap-2">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {t("settings.profile.sessions.title", "Active sessions")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t(
                "settings.profile.sessions.description",
                "Review signed-in devices and revoke access you no longer recognize.",
              )}
            </p>
          </div>
        </div>

        <div className={listStackClasses}>
          {isLoading ? (
            <SkeletonList count={3} />
          ) : sessions.length === 0 ? (
            <p className={placeholderClasses}>
              {t("settings.profile.sessions.empty", "No active sessions were found.")}
            </p>
          ) : (
            sessions.map((session) => {
              const lastActive = session.last_used_at
                ? new Date(session.last_used_at).toLocaleString()
                : t("settings.profile.sessions.unknown_activity", "Unknown activity");
              return (
                <div key={session.id} className={rowClasses}>
                  <div>
                    <div className={metaTitleClasses}>
                      <strong className="text-lg font-semibold">
                        {session.device_label ?? session.platform.toUpperCase()}
                      </strong>
                      <span className={pillClasses}>
                        {session.platform.toUpperCase()}
                      </span>
                    </div>
                    <small className={metaSmallClasses}>
                      {t("settings.profile.sessions.last_active", {
                        defaultValue: "Last active {{date}}",
                        date: lastActive
                      })}
                    </small>
                    {session.ip_at_last_use && (
                      <small className={metaSmallClasses}>
                        {t("settings.profile.sessions.ip_label", {
                          defaultValue: "Recent IP {{ip}}",
                          ip: session.ip_at_last_use
                        })}
                      </small>
                    )}
                    <small className={metaSmallClasses}>
                      {t("settings.profile.sessions.expires_at", {
                        defaultValue: "Session ends {{date}}",
                        date: new Date(session.refresh_expires_at).toLocaleString()
                      })}
                    </small>
                  </div>
                  <button
                    type="button"
                    className={revokeButtonClasses}
                    onClick={() => {
                      const isCurrentSession = session.id === sessionId;
                      if (isCurrentSession) {
                        setSessionToConfirm(session);
                        return;
                      }
                      void handleRevokeSession(session.id);
                    }}
                    disabled={isRevokingAll || revokingSessionId === session.id}
                  >
                    {revokingSessionId === session.id
                      ? t("settings.profile.sessions.revoking", "Revoking…")
                      : t("settings.profile.sessions.revoke", "Revoke")}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </section>

      <ConfirmDialog
        open={showRevokeAllConfirm}
        title={t(
          "settings.profile.sessions.revoke_all_confirm.title",
          "Sign out of all devices?"
        )}
        description={t(
          "settings.profile.sessions.revoke_all_confirm.message",
          "This will end every active session across all of your devices. You’ll need to sign in again on those devices."
        )}
        confirmLabel={t(
          "settings.profile.sessions.revoke_all_confirm.confirm",
          "Sign out everywhere"
        )}
        confirmLoadingLabel={t(
          "settings.profile.sessions.revoking_all",
          "Signing out…"
        )}
        cancelLabel={t(
          "settings.profile.sessions.revoke_all_confirm.cancel",
          "Keep me signed in"
        )}
        confirmTone="danger"
        confirmLoading={isRevokingAll}
        onConfirm={confirmRevokeAllSessions}
        onCancel={() => setShowRevokeAllConfirm(false)}
      />
      <ConfirmDialog
        open={Boolean(sessionToConfirm)}
        title={t(
          "settings.profile.sessions.revoke_current_confirm.title",
          "Sign out on this device?"
        )}
        description={t(
          "settings.profile.sessions.revoke_current_confirm.message",
          "You'll be signed out here immediately. Continue?"
        )}
        confirmLabel={t(
          "settings.profile.sessions.revoke_current_confirm.confirm",
          "Sign out on this device"
        )}
        confirmLoadingLabel={t("settings.profile.sessions.revoking", "Revoking…")}
        cancelLabel={t(
          "settings.profile.sessions.revoke_current_confirm.cancel",
          "Stay signed in"
        )}
        confirmTone="danger"
        confirmLoading={
          Boolean(sessionToConfirm?.id && revokingSessionId === sessionToConfirm.id)
        }
        onConfirm={async () => {
          if (sessionToConfirm) {
            setSessionToConfirm(null);
            await handleRevokeSession(sessionToConfirm.id, { redirectOnRevoke: true });
          }
        }}
        onCancel={() => setSessionToConfirm(null)}
      />
    </PageShell>
  );
}
